/**
 * <clp-review-card>
 * Detects when the review body overflows the line-clamp and reveals
 * a "Read more" toggle. Clicking the toggle expands the card inline.
 *
 * Markup contract:
 *   <clp-review-card>
 *     <div class="clp-review-body-wrap" data-clp-review-clamp>
 *       <blockquote class="clp-review-body">...</blockquote>
 *       <button data-clp-review-toggle data-more-label data-less-label hidden>
 *         Read more
 *       </button>
 *     </div>
 *   </clp-review-card>
 *
 * Reacts to layout changes via ResizeObserver — necessary so the
 * toggle appears/disappears correctly when the theme editor resizes
 * the iframe or when fonts finish loading.
 */
class ClpReviewCard extends HTMLElement {
  constructor() {
    super();
    this.handleToggle = this.handleToggle.bind(this);
    this.checkOverflow = this.checkOverflow.bind(this);
  }

  connectedCallback() {
    this.body = this.querySelector('.clp-review-body');
    this.toggle = this.querySelector('[data-clp-review-toggle]');
    this.clampHost = this.querySelector('[data-clp-review-clamp]');

    if (!this.body || !this.clampHost) return;

    if (this.toggle) {
      this.toggle.addEventListener('click', this.handleToggle);
    }

    // Initial check (next frame so layout has settled).
    requestAnimationFrame(this.checkOverflow);

    // Re-check on resize. The clamp is line-based, so column width
    // changes affect whether content overflows.
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.checkOverflow);
      this.resizeObserver.observe(this);
    }

    // Re-check once webfonts have loaded — they often change line
    // height enough to flip overflow state.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this.checkOverflow());
    }
  }

  disconnectedCallback() {
    if (this.toggle) {
      this.toggle.removeEventListener('click', this.handleToggle);
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  checkOverflow() {
    if (!this.body || !this.toggle) return;

    // Skip overflow detection while expanded — the body is unclamped
    // so scrollHeight will naturally exceed clientHeight.
    if (this.hasAttribute('data-clp-expanded')) return;

    const overflows = this.body.scrollHeight - this.body.clientHeight > 1;

    if (overflows) {
      this.setAttribute('data-clp-overflow', '');
      this.toggle.hidden = false;
    } else {
      this.removeAttribute('data-clp-overflow');
      this.toggle.hidden = true;
    }
  }

  handleToggle() {
    const expanded = this.toggleAttribute('data-clp-expanded');
    this.toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');

    const moreLabel = this.toggle.dataset.moreLabel || 'Read more';
    const lessLabel = this.toggle.dataset.lessLabel || 'Show less';
    this.toggle.textContent = expanded ? lessLabel : moreLabel;
  }
}

if (!customElements.get('clp-review-card')) {
  customElements.define('clp-review-card', ClpReviewCard);
}
